// @ts-check
/**
 * EdocRawView.js (Factory Pattern)
 *
 * Specialized raw view for EDoc-EDoc ReferenceUnit.
 * Displays the full text content of an electronic document.
 */

/** @typedef {import('../../../../stores/referenceUnitStore').ReferenceUnitHandle} ReferenceUnitHandle */

/**
 * @param {any} deps - Injected Vue/store dependencies, see componentRegistry.js.
 *   Typed as `any` to preserve the factory-pattern escape hatch for
 *   dynamically-loaded configurable types.
 */
export default function createComponent(deps) {
    const { ref, watch, onMounted, useReferenceUnitStore, useMatterStore, EdocTextDisplay } = deps;

    return {
        name: 'EdocRawView',

        components: {
            EdocTextDisplay,
        },

        props: {
            reference: {
                type: Object,
                required: true,
            },
        },

        /** @param {{ reference: ReferenceUnitHandle }} props */
        setup(props) {
            const referenceUnitStore = useReferenceUnitStore();
            const matterStore = useMatterStore();

            const loading = ref(false);
            const error = ref(null);
            const rawViewModel = ref(null);

            async function fetchRawViewModel() {
                if (!props.reference) {
                    console.warn('[EdocRawView] No reference provided');
                    return;
                }

                // Serve a cached raw-view model synchronously (re-selecting or
                // navigating back to a reference is then instant, no refetch /
                // loading flash).
                const matterId = matterStore.currentMatter?.RecordId;
                const cached = matterId
                    ? referenceUnitStore.getCachedRawViewModel(props.reference, matterId)
                    : null;
                if (cached) {
                    rawViewModel.value = cached;
                    error.value = null;
                    loading.value = false;
                    return;
                }

                loading.value = true;
                error.value = null;

                try {
                    console.log('[EdocRawView] Fetching raw view for:', props.reference);

                    rawViewModel.value = await referenceUnitStore.fetchRawViewModel(
                        props.reference,
                        matterStore.currentMatter.RecordId,
                    );

                    console.log('[EdocRawView] Loaded raw view model:', rawViewModel.value);
                } catch (err) {
                    console.error('[EdocRawView] Error fetching raw view:', err);
                    error.value = err instanceof Error ? err.message : String(err);
                } finally {
                    loading.value = false;
                }
            }

            onMounted(() => {
                fetchRawViewModel();
            });

            watch(
                () => props.reference,
                () => {
                    fetchRawViewModel();
                },
            );

            return {
                loading,
                error,
                rawViewModel,
            };
        },

        template: `
            <div class="communication-raw-view-content edoc-raw-view-content">
                <!-- Loading state -->
                <div v-if="loading" class="loading-state">
                    <div class="loading-dots"><span></span><span></span><span></span></div>
                </div>

                <!-- Error state -->
                <div v-else-if="error" class="error-state">
                    <p>Error loading document: {{ error }}</p>
                </div>

                <!-- Document content -->
                <div v-else-if="rawViewModel" class="edoc-content">
                    <EdocTextDisplay
                        :text="rawViewModel.text"
                        :documentIdentifier="rawViewModel.document_identifier"
                        :isHit="rawViewModel.hit !== false"
                    />

                    <!-- Empty state -->
                    <div v-if="!rawViewModel.text" class="structured-empty-state">
                        No text content found in this document.
                    </div>
                </div>
            </div>
        `,
    };
}
